import { useState } from 'react'
import { Download, Upload, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import {
  usePreviewMeterReadingImport,
  useConfirmMeterReadingImport,
} from '@/hooks/use-meter-readings'
import { getApiErrorMessage } from '@/lib/api'
import {
  downloadMeterReadingTemplateCsv,
  parseMeterReadingImportFile,
} from '@/lib/meter-readings-import'
import type {
  MeterReadingImportPreviewResult,
  MeterReadingImportRowInput,
  PlotReadingStatus,
  UtilityType,
} from '@riverpark/types'

interface BulkReadingUploadModalProps {
  open: boolean
  onClose: () => void
  utilityType: UtilityType
  billingPeriod: string
  plots: PlotReadingStatus[]
}

type Step = 'upload' | 'preview' | 'done'

export function BulkReadingUploadModal({
  open,
  onClose,
  utilityType,
  billingPeriod,
  plots,
}: BulkReadingUploadModalProps) {
  const preview = usePreviewMeterReadingImport()
  const confirm = useConfirmMeterReadingImport(utilityType.id, billingPeriod)

  const [step, setStep] = useState<Step>('upload')
  const [rows, setRows] = useState<MeterReadingImportRowInput[]>([])
  const [result, setResult] = useState<MeterReadingImportPreviewResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  function reset() {
    setStep('upload')
    setRows([])
    setResult(null)
    setError(null)
  }

  function handleClose() {
    reset()
    onClose()
  }

  async function handleFileSelect(file: File) {
    setError(null)
    try {
      const parsedRows = await parseMeterReadingImportFile(file)
      const { data } = await preview.mutateAsync({
        utilityTypeId: utilityType.id,
        billingPeriod,
        rows: parsedRows,
      })
      setRows(parsedRows)
      setResult(data)
      setStep('preview')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not read that file'))
    }
  }

  async function handleConfirm() {
    setError(null)
    try {
      await confirm.mutateAsync({ utilityTypeId: utilityType.id, billingPeriod, rows })
      setStep('done')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to import readings'))
    }
  }

  return (
    <Modal open={open} onClose={handleClose} labelledBy="bulk-upload-modal-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              {utilityType.name} — {billingPeriod}
            </p>
            <h2 id="bulk-upload-modal-title" className="font-serif text-xl text-ink">
              Bulk upload readings
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <div className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          {step === 'upload' && (
            <>
              <p className="font-sans text-sm text-muted-foreground">
                Download the template, fill in previous and current readings offline, then upload it
                here. A photo is still required per plot afterwards.
              </p>
              <Button
                variant="secondary"
                type="button"
                onClick={() =>
                  downloadMeterReadingTemplateCsv(utilityType.name, billingPeriod, plots)
                }
              >
                <Download size={14} className="mr-1.5" />
                Download template
              </Button>

              <label className="flex cursor-pointer flex-col items-center gap-2 rounded border border-dashed border-border-2 px-4 py-8 text-center hover:border-ink">
                <Upload size={20} className="text-muted-foreground" />
                <span className="font-sans text-sm text-ink">Choose a completed CSV file</span>
                <input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) void handleFileSelect(file)
                    e.target.value = ''
                  }}
                />
              </label>

              {preview.isPending && (
                <p className="font-sans text-sm text-muted-foreground">Validating…</p>
              )}
            </>
          )}

          {step === 'preview' && result && (
            <>
              <div className="flex flex-wrap gap-3 font-sans text-sm">
                <span className="text-ink">{result.summary.total} rows</span>
                <span className="text-primary">{result.summary.valid} valid</span>
                {result.summary.invalid > 0 && (
                  <span className="text-destructive">{result.summary.invalid} invalid</span>
                )}
              </div>
              {result.summary.missingPlots.length > 0 && (
                <p className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-xs text-muted-foreground">
                  Not included in this file: {result.summary.missingPlots.join(', ')}
                </p>
              )}

              <div className="overflow-x-auto rounded border border-border-2">
                <table className="w-full font-sans text-sm">
                  <thead>
                    <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                      <th className="px-3 py-2">Plot</th>
                      <th className="px-3 py-2">Previous</th>
                      <th className="px-3 py-2">Current</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((row) => (
                      <tr key={row.rowId} className="border-b border-border-2 last:border-0">
                        <td className="px-3 py-2 font-medium text-ink">{row.plotNewNumber}</td>
                        <td className="px-3 py-2 text-ink">{row.previousReading ?? '—'}</td>
                        <td className="px-3 py-2 text-ink">{row.currentReading ?? '—'}</td>
                        <td className="px-3 py-2">
                          <Badge variant={row.status === 'valid' ? 'paid' : 'overdue'}>
                            {row.status === 'valid' ? 'Valid' : 'Invalid'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {row.errors.join('; ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {step === 'done' && (
            <p className="font-sans text-sm text-ink">
              Import complete. Readings are saved as drafts awaiting a photo — attach one from the
              plot list to finish each.
            </p>
          )}

          {error && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          {step === 'preview' && (
            <>
              <Button type="button" variant="secondary" onClick={reset}>
                Choose a different file
              </Button>
              <Button
                type="button"
                loading={confirm.isPending}
                disabled={result?.summary.valid === 0}
                onClick={() => void handleConfirm()}
              >
                Import {result?.summary.valid ?? 0} reading(s)
              </Button>
            </>
          )}
          {step !== 'preview' && (
            <Button type="button" variant="secondary" onClick={handleClose}>
              {step === 'done' ? 'Done' : 'Cancel'}
            </Button>
          )}
        </ModalFooter>
      </div>
    </Modal>
  )
}
